import { isApiError } from '@central/ui';
import { Button, PasswordInput } from '@mantine/core';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useApi } from '../api';
import { FactorSteps } from '../components/FactorSteps';
import { ErrorText, Panel, SignInPage } from '../components/signInParts';
import { amberButton, bodyText, inputStyles } from '../components/signInStyles';
import { errorMessage } from '../lib/errors';
import { useSignedIn } from '../lib/session';
import { nextStep, type FactorStep } from '../lib/signInSteps';

const MIN_PASSWORD = 12;
const MAX_PASSWORD = 256;

/**
 * /setup#<token>: the link from POST /staff, /staff/{id}/setup-link or
 * /recovery-link. The token rides in the fragment, which the browser never
 * sends to a server; it is read once, dropped from the address bar, and
 * POSTed in the body of /auth/setup. Then the user verifies a factor they kept
 * or enrolls a first one.
 */
export function Setup() {
  const api = useApi();
  const signedIn = useSignedIn();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [token] = useState(() => hash.replace(/^#/, ''));
  const [step, setStep] = useState<FactorStep | { kind: 'password' }>({ kind: 'password' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (hash) void navigate({ to: '/setup', hash: '', replace: true });
  }, [hash, navigate]);

  const submit = async () => {
    setError(undefined);
    setFieldError(undefined);
    // The server counts characters (code points), not UTF-16 units.
    const length = [...password].length;
    if (length < MIN_PASSWORD) return setFieldError(`Use at least ${MIN_PASSWORD} characters.`);
    if (length > MAX_PASSWORD) return setFieldError(`Use at most ${MAX_PASSWORD} characters.`);
    if (password !== confirm) return setFieldError('The two passwords do not match.');
    setBusy(true);
    try {
      const res = await api.auth.setup({ token, password });
      setPassword('');
      setConfirm('');
      setStep(nextStep(res.methods));
    } catch (e) {
      if (isApiError(e, 'invalid') && e.fields?.password)
        setFieldError(`The password needs ${e.fields.password}.`);
      else
        setError(
          errorMessage(e, {
            invalid_token:
              'This setup link has been used or has expired. Ask an Owner for a new one.',
          }),
        );
    } finally {
      setBusy(false);
    }
  };

  if (step.kind !== 'password') {
    return (
      <SignInPage>
        <FactorSteps
          step={step}
          done={1}
          onStep={setStep}
          onStartOver={() => void navigate({ to: '/sign-in' })}
          onSignedIn={signedIn}
        />
      </SignInPage>
    );
  }

  return (
    <SignInPage>
      <Panel step={1} of={3} title="Choose a password" onSubmit={() => void submit()}>
        {token ? (
          <>
            <p style={bodyText}>
              This link works once and for 24 hours. Next you will set up a passkey or an
              authenticator app.
            </p>
            <PasswordInput
              label="New password"
              description={`At least ${MIN_PASSWORD} characters.`}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              styles={inputStyles}
            />
            <PasswordInput
              label="Repeat the password"
              autoComplete="new-password"
              required
              value={confirm}
              error={fieldError}
              onChange={(e) => setConfirm(e.currentTarget.value)}
              styles={inputStyles}
            />
            <ErrorText>{error}</ErrorText>
            <Button type="submit" fullWidth h={44} loading={busy} style={amberButton}>
              Set password
            </Button>
          </>
        ) : (
          <ErrorText>
            This setup link is incomplete. Open the whole link you were sent, or ask an Owner for a
            new one.
          </ErrorText>
        )}
      </Panel>
    </SignInPage>
  );
}
